import { useCallback, type FC } from "react";
import { CopyOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../../../utils/constants/AppStrings";

import type { CreatePromoCodeFormValues } from "../CreatePromoCodeModal/createPromoCodeModalData";

import styles from "./PromoCodeListItem.module.css";

export type PromoCodeListItemProps = {
  /** 1-based index used to label the row ("Promo code 1", "Promo code 2"). */
  index: number;
  promoCode: CreatePromoCodeFormValues;
  onEdit?: () => void;
  onDuplicate?: () => void;
  /**
   * Override the clipboard write. Defaults to `navigator.clipboard.writeText`
   * — exposed for tests and for environments where the API isn't available.
   */
  onCopy?: (url: string) => void;
};

const PromoCodeListItem: FC<PromoCodeListItemProps> = ({
  index,
  promoCode,
  onEdit,
  onDuplicate,
  onCopy,
}) => {
  const trackedUrl = promoCode.deepLinkUrl;

  const handleCopy = useCallback(() => {
    if (onCopy) {
      onCopy(trackedUrl);
      return;
    }
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      // Best-effort copy; we don't surface a toast here — the parent step
      // can layer one on if/when product requests it.
      void navigator.clipboard.writeText(trackedUrl);
    }
  }, [onCopy, trackedUrl]);

  const labelId = `new-affiliate-promo-code-${index}-label`;

  return (
    <li
      className={styles.item}
      data-testid={`new-affiliate-promo-code-item-${index}`}
    >
      <div className={styles.header}>
        <span
          id={labelId}
          className={styles.label}
        >{`${AppStrings.NEW_AFFILIATE_PROMO_CODE_LABEL} ${index}`}</span>
        {onEdit && (
          <button
            type="button"
            className={styles.linkAction}
            onClick={onEdit}
            data-testid={`new-affiliate-promo-code-item-${index}-edit`}
          >
            {AppStrings.NEW_AFFILIATE_PROMO_CODE_EDIT}
          </button>
        )}
      </div>

      <div
        className={styles.card}
        aria-labelledby={labelId}
        data-testid={`new-affiliate-promo-code-item-${index}-card`}
      >
        <div className={styles.cardText}>
          <span className={styles.name}>{promoCode.name}</span>
          {trackedUrl && (
            <span className={styles.url}>{trackedUrl}</span>
          )}
        </div>
        {trackedUrl && (
          <button
            type="button"
            className={styles.copyButton}
            onClick={handleCopy}
            aria-label={AppStrings.NEW_AFFILIATE_PROMO_CODE_COPY_URL_LABEL}
            data-testid={`new-affiliate-promo-code-item-${index}-copy`}
          >
            <CopyOutlined aria-hidden="true" />
          </button>
        )}
      </div>

      {onDuplicate && (
        <button
          type="button"
          className={styles.linkAction}
          onClick={onDuplicate}
          data-testid={`new-affiliate-promo-code-item-${index}-duplicate`}
        >
          <CopyOutlined aria-hidden="true" />
          {AppStrings.NEW_AFFILIATE_PROMO_CODE_DUPLICATE}
        </button>
      )}
    </li>
  );
};

export default PromoCodeListItem;
